/**
 * PartnerLogo — Affiche le logo officiel d'un partenaire avec fallback robuste
 *
 * Stratégie multi-sources (en cascade) :
 *   1. Asset local /assets/logos/{partner}.png (si présent)         → instantané, offline
 *   2. URL CDN n°1 (Wikipedia Commons / site officiel)              → fiable, public
 *   3. URL CDN n°2 (Brandfetch / fallback)                          → backup
 *   4. Emoji distinctif                                             → toujours OK
 *
 * Pour ajouter un logo en local (recommandé pour fiabilité production) :
 *   1. Télécharger le logo PNG (ex: 128x128) depuis le site officiel
 *   2. Le placer dans /assets/logos/{partner}.png (ex: tous.png)
 *   3. Ajouter le require dans LOCAL_LOGOS ci-dessous
 *
 * Usage :
 *   <PartnerLogo partner="tous" size={20} />
 */
import React, { useState } from 'react';
import { View, Text, Image, StyleSheet } from 'react-native';

// ─── Logos LOCAUX (priorité 1) ─────────────────────────────────
// Décommenter quand les fichiers PNG seront ajoutés dans /assets/logos/
// require() est résolu au build time, donc ces fichiers DOIVENT exister.
const LOCAL_LOGOS = {
  // tous:     require('../../assets/logos/tous.png'),
  // iqueens:  require('../../assets/logos/iqueens.png'),
  // cashmere: require('../../assets/logos/cashmere.png'),
  // zalando:  require('../../assets/logos/zalando.png'),
  // asos:     require('../../assets/logos/asos.png'),
  // hm:       require('../../assets/logos/hm.png'),
  // zara:     require('../../assets/logos/zara.png'),
  // amazon:   require('../../assets/logos/amazon.png'),
  // mango:    require('../../assets/logos/mango.png'),
};

// ─── URLs CDN (priorité 2 si pas de local) ─────────────────────
// Sources stables et publiques. Ordre = primary, secondary.
// v1.12.343 — clearbit.com a été fermé en 2024 → toutes les URLs clearbit retiré.
//             Source unique fiable et gratuite : Google s2/favicons.
const REMOTE_LOGOS = {
  tous: {
    urls: [
      'https://www.google.com/s2/favicons?domain=tous.com&sz=128',
      'https://upload.wikimedia.org/wikipedia/commons/thumb/4/45/Tous_company_logo.png/640px-Tous_company_logo.png',
    ],
    fallback: '🐻',
  },
  iqueens: {
    urls: [
      'https://www.google.com/s2/favicons?domain=iqueens.fr&sz=128',
    ],
    fallback: '👑',
  },
  cashmere: {
    urls: [
      'https://www.google.com/s2/favicons?domain=cashmereboutique.com&sz=128',
    ],
    fallback: '🧶',
  },
  zalando: {
    urls: [
      'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Zalando_logo.svg/2560px-Zalando_logo.svg.png',
      'https://www.google.com/s2/favicons?domain=zalando.fr&sz=128',
    ],
    fallback: '🟠',
  },
  asos: {
    urls: [
      'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Asos_logo.svg/2560px-Asos_logo.svg.png',
      'https://www.google.com/s2/favicons?domain=asos.com&sz=128',
    ],
    fallback: '⬛',
  },
  hm: {
    urls: [
      'https://upload.wikimedia.org/wikipedia/commons/thumb/5/53/H%26M-Logo.svg/2560px-H%26M-Logo.svg.png',
      'https://www.google.com/s2/favicons?domain=hm.com&sz=128',
    ],
    fallback: '🔴',
  },
  zara: {
    urls: [
      'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fd/Zara_Logo.svg/2560px-Zara_Logo.svg.png',
      'https://www.google.com/s2/favicons?domain=zara.com&sz=128',
    ],
    fallback: '⬛',
  },
  amazon: {
    urls: [
      'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4a/Amazon_icon.svg/1024px-Amazon_icon.svg.png',
      'https://www.google.com/s2/favicons?domain=amazon.fr&sz=128',
    ],
    fallback: '🟧',
  },
  mango: {
    urls: [
      'https://upload.wikimedia.org/wikipedia/commons/thumb/1/16/Mango_logo.svg/2560px-Mango_logo.svg.png',
      'https://www.google.com/s2/favicons?domain=mango.com&sz=128',
    ],
    fallback: '🟤',
  },
  // v1.12.293 — partenaire Suit Negozi (Rakuten Advertising, MID 53537)
  suit_negozi: {
    urls: [
      'https://www.google.com/s2/favicons?domain=suitnegozi.com&sz=128',
    ],
    fallback: '🤵',
  },
  // v1.12.322 — partenaire Ann Demeulemeester (Rakuten Advertising, MID 53588)
  ann_demeulemeester: {
    urls: [
      'https://www.google.com/s2/favicons?domain=anndemeulemeester.com&sz=128',
    ],
    fallback: '◾',
  },
  // v1.12.336 — partenaire COUTR (Rakuten Advertising, MID 54152, USD)
  //   v1.12.338 — clearbit ne résout pas coutr.com → fallback texte direct
  coutr: {
    urls: [
      'https://www.google.com/s2/favicons?domain=coutr.com&sz=128',
    ],
    fallback: '🇺🇸',
  },
};

// v1.12.293 — Labels courts pour l'affichage compact dans les cards/badges.
// Permet d'afficher "SUIT" au lieu de "Suit Negozi", "TOUS" au lieu de "tous", etc.
export const PARTNER_SHORT_LABELS = {
  suit_negozi:        'SUIT',
  ann_demeulemeester: 'ANN',   // v1.12.322
  coutr:              'COUTR', // v1.12.336
};

export function getPartnerShortLabel(partner) {
  return PARTNER_SHORT_LABELS[partner] || (partner ? String(partner).toUpperCase() : '');
}

export default function PartnerLogo({ partner, size = 18, style }) {
  // ═══ Index de l'URL en cours d'essai ═══
  // 0 = URL primary, 1 = secondary, -1 = afficher fallback emoji
  const [urlIndex, setUrlIndex] = useState(0);

  // ═══ 1. Asset LOCAL (priorité 1) ═══
  const localAsset = LOCAL_LOGOS[partner];
  if (localAsset) {
    return (
      <Image
        source={localAsset}
        style={[
          { width: size, height: size, resizeMode: 'contain' },
          style,
        ]}
      />
    );
  }

  // ═══ 2/3. URLs CDN (avec fallback en cascade) ═══
  const cfg = REMOTE_LOGOS[partner];
  if (cfg && urlIndex >= 0 && urlIndex < cfg.urls.length) {
    return (
      <Image
        source={{ uri: cfg.urls[urlIndex] }}
        style={[
          { width: size, height: size, resizeMode: 'contain' },
          style,
        ]}
        onError={() => {
          // Essayer l'URL suivante. Si plus rien → -1 = emoji
          setUrlIndex(prev => prev + 1 < cfg.urls.length ? prev + 1 : -1);
        }}
      />
    );
  }

  // ═══ 4. Emoji fallback ═══
  const emoji = cfg?.fallback || (partner || '?')[0].toUpperCase();
  return (
    <View style={[s.fallbackWrap, { width: size, height: size }, style]}>
      <Text style={[s.fallbackEmoji, { fontSize: size * 0.85 }]}>{emoji}</Text>
    </View>
  );
}

const s = StyleSheet.create({
  fallbackWrap: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  fallbackEmoji: {
    textAlign: 'center',
  },
});
